"use client";

import { useState, useSyncExternalStore } from "react";

interface RashifalAudioPlayerProps {
  textToSpeak: string;
  rashiName: string;
  isHindi?: boolean;
  className?: string;
}

// A browser capability is NOT knowable during the hydration render. Reading it
// there returns false on the server and true in the browser for the SAME render
// pass, so a component that gates `return null` on it emits an element the
// server never sent -- a structural mismatch (React #418). App Router hydrates
// the whole document, so ONE such mismatch regenerates the entire root, which
// is what produced the trashed head and made #1791/#1785; see VoiceQueryInput look like head
// injection for weeks.
//
// useSyncExternalStore is React's answer: it takes an explicit SERVER snapshot,
// so the first client render provably matches the server, and the real value
// arrives after mount. A capability never changes within a session, so the
// subscription is a no-op -- but it must be module-level and stable, or React
// resubscribes every render.
const NO_CHANGE = () => () => {};
const SERVER_SNAPSHOT = () => false;

export default function RashifalAudioPlayer({
  textToSpeak,
  rashiName,
  isHindi = false,
  className = "",
}: RashifalAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const isSupported = useSyncExternalStore(
    NO_CHANGE,
    () => "speechSynthesis" in window,
    SERVER_SNAPSHOT
  );

  const handleTogglePlay = () => {
    if (!isSupported || typeof window === "undefined") return;

    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    } else {
      window.speechSynthesis.cancel(); // Clear existing queue
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      utterance.lang = isHindi ? "hi-IN" : "en-IN";
      utterance.rate = 0.95; // Gentle speaking pace

      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);

      window.speechSynthesis.speak(utterance);
      setIsPlaying(true);
    }
  };

  if (!isSupported) return null;

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-xl border border-border-default bg-surface-raised px-3.5 py-2 shadow-xs ${className}`}
    >
      <button
        type="button"
        onClick={handleTogglePlay}
        className="flex items-center gap-2 text-caption font-bold text-text-primary hover:text-intent-brand transition-colors"
        aria-label={
          isPlaying ? `Pause ${rashiName} audio forecast` : `Listen to ${rashiName} audio forecast`
        }
      >
        <span className="text-base">{isPlaying ? "⏸️" : "🔊"}</span>
        <span>
          {isPlaying
            ? isHindi
              ? "ऑडियो रोकें"
              : "Pause Audio"
            : isHindi
              ? `60-सेकंड राशिफल सुनें (${rashiName})`
              : `Listen to 60s Forecast (${rashiName})`}
        </span>
      </button>

      {isPlaying && (
        <span className="flex items-center gap-0.5 ml-1">
          <span className="w-1 h-3 bg-intent-brand rounded-full animate-bounce" />
          <span className="w-1 h-4 bg-intent-brand rounded-full animate-bounce delay-100" />
          <span className="w-1 h-2 bg-intent-brand rounded-full animate-bounce delay-200" />
        </span>
      )}
    </div>
  );
}
